import React, { useEffect, useState } from 'react';
import Reveal from '../Reveal';
import { aboutImage, features } from '../../mock';

export default function ContactSection() {
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    interest: features[0].label,
    budget: '',
    message: '',
  });
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');

  // Pick up a release/interest clicked from the Upcoming Releases grid
  // or any Feature section's "Discover More" link.
  useEffect(() => {
    const poll = () => {
      try {
        const selectedRelease = sessionStorage.getItem('rb_selected_release');
        if (selectedRelease) {
          sessionStorage.removeItem('rb_selected_release');
          setForm((f) => ({
            ...f,
            interest: 'Upcoming Release',
            message: f.message
              ? f.message
              : `I would like to enquire about: ${selectedRelease}.`,
          }));
        }
        const selectedInterest = sessionStorage.getItem('rb_selected_interest');
        if (selectedInterest) {
          sessionStorage.removeItem('rb_selected_interest');
          setForm((f) => ({ ...f, interest: selectedInterest }));
        }
      } catch (e) {
        // ignore
      }
    };
    poll();
    window.addEventListener('focus', poll);
    const iv = setInterval(poll, 600);
    return () => {
      window.removeEventListener('focus', poll);
      clearInterval(iv);
    };
  }, []);

  const onChange = (e) =>
    setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = (e) => {
    e.preventDefault();
    setError('');
    if (!form.firstName || !form.email || !form.message) {
      setError('Please complete the required fields.');
      return;
    }
    const existing = JSON.parse(
      localStorage.getItem('rb_enquiries') || '[]'
    );
    existing.push({ ...form, at: new Date().toISOString() });
    localStorage.setItem('rb_enquiries', JSON.stringify(existing));
    setSent(true);
    setForm({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      interest: features[0].label,
      budget: '',
      message: '',
    });
    setTimeout(() => setSent(false), 6000);
  };

  return (
    <section id="contact" className="bg-[#f4f1eb] py-24 md:py-36">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-14 md:gap-20">
          <Reveal>
            <div className="font-sans-ui text-[10px] text-[#5a5852] mb-6">
              Contact
            </div>
            <h2 className="font-serif-display text-[46px] md:text-[68px] leading-[1] tracking-wide lowercase text-[#2b2a27]">
              begin a
              <br />
              <em className="italic font-light">conversation</em>
            </h2>
            <p className="mt-8 max-w-md text-[15px] leading-[1.85] text-[#5a5852]">
              Whether you are considering a new residence, a private
              development, or a careful renovation, we would be glad to hear
              from you. A founding partner personally reviews every enquiry.
            </p>

            <div className="mt-12 space-y-6">
              <div>
                <div className="font-sans-ui text-[10px] text-[#5a5852] mb-2">
                  Email
                </div>
                <a
                  href="mailto:info@rivabuildgroup.com"
                  className="font-serif-display italic text-[19px] text-[#2b2a27] underline-anim"
                >
                  info@rivabuildgroup.com
                </a>
              </div>
              <div>
                <div className="font-sans-ui text-[10px] text-[#5a5852] mb-2">
                  Studio
                </div>
                <div className="text-[14px] text-[#2b2a27] leading-relaxed">
                  9200 Sunset Boulevard
                  <br />
                  West Hollywood, CA 90069
                </div>
              </div>
              <div>
                <div className="font-sans-ui text-[10px] text-[#5a5852] mb-2">
                  Hours
                </div>
                <div className="text-[14px] text-[#2b2a27]">
                  Monday &ndash; Friday, 9:00 &ndash; 18:00 PST
                </div>
              </div>
            </div>
          </Reveal>

          <Reveal delay={150}>
            <form onSubmit={onSubmit} className="space-y-6">
              <div className="grid grid-cols-2 gap-6">
                <Field
                  name="firstName"
                  label="First Name *"
                  value={form.firstName}
                  onChange={onChange}
                />
                <Field
                  name="lastName"
                  label="Last Name"
                  value={form.lastName}
                  onChange={onChange}
                  required={false}
                />
              </div>
              <Field
                type="email"
                name="email"
                label="Email *"
                value={form.email}
                onChange={onChange}
              />
              <div className="grid grid-cols-2 gap-6">
                <Field
                  name="phone"
                  label="Phone"
                  value={form.phone}
                  onChange={onChange}
                  required={false}
                />
                <Field
                  name="budget"
                  label="Approx. Budget"
                  value={form.budget}
                  onChange={onChange}
                  required={false}
                />
              </div>

              <div>
                <label className="font-sans-ui text-[10px] text-[#5a5852] block mb-2">
                  Service of Interest
                </label>
                <select
                  name="interest"
                  value={form.interest}
                  onChange={onChange}
                  className="w-full bg-transparent border-b border-[#c9c3b6] py-2 text-[14px] focus:border-[#2b2a27] outline-none"
                >
                  {features.map((s) => (
                    <option key={s.id} value={s.label}>
                      {s.label}
                    </option>
                  ))}
                  <option value="Upcoming Release">Upcoming Release</option>
                  <option value="General Enquiry">General Enquiry</option>
                </select>
              </div>

              <div>
                <label className="font-sans-ui text-[10px] text-[#5a5852] block mb-2">
                  Tell us about your project *
                </label>
                <textarea
                  name="message"
                  rows="4"
                  value={form.message}
                  onChange={onChange}
                  className="w-full bg-transparent border-b border-[#c9c3b6] py-2 text-[14px] focus:border-[#2b2a27] outline-none resize-none"
                />
              </div>

              {error && (
                <div className="text-[13px] text-[#a43d2e]">{error}</div>
              )}
              {sent && (
                <div className="font-serif-display italic text-[15px] text-[#2b2a27]">
                  Thank you &mdash; we will be in touch shortly.
                </div>
              )}

              <button
                type="submit"
                className="mt-2 border border-[#2b2a27] px-10 py-4 font-sans-ui text-[11px] tracking-[0.3em] hover:bg-[#2b2a27] hover:text-[#f4f1eb] transition-colors"
              >
                Send Enquiry
              </button>
            </form>
          </Reveal>
        </div>

        <div className="mt-24 md:mt-32 aspect-[21/9] overflow-hidden image-zoom">
          <img
            src={aboutImage}
            alt="Riva Build Group studio"
            className="w-full h-full object-cover"
            loading="lazy"
          />
        </div>
      </div>
    </section>
  );
}

function Field({ label, name, value, onChange, type = 'text', required = true }) {
  return (
    <div>
      <label className="font-sans-ui text-[10px] text-[#5a5852] block mb-2">
        {label}
      </label>
      <input
        type={type}
        name={name}
        value={value}
        onChange={onChange}
        required={required}
        className="w-full bg-transparent border-b border-[#c9c3b6] py-2 text-[14px] focus:border-[#2b2a27] outline-none"
      />
    </div>
  );
}
